import { ActionIcon, Button, Modal, Skeleton, Tabs, Tooltip, UnstyledButton } from '@mantine/core';
import {
  IconAlertTriangle,
  IconBell,
  IconCalendarEvent,
  IconCalendarStats,
  IconChecklist,
  IconDownload,
  IconEye,
  IconFileCertificate,
  IconFileDescription,
  IconFileTypePdf,
  IconLink,
  IconMessagePlus,
  IconPaperclip,
  IconPhoto,
  IconPlus,
  IconRefresh,
  IconRosetteDiscountCheck,
  IconStackFront,
  IconZoomCheck,
} from '@tabler/icons-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { formatDay } from '../../format.js';
import { Panel } from '../../Panel.js';
import { StatusBadge } from '../../StatusBadge.js';
import { formatFileSize } from '../documents/library.js';
import { CertificateStatus, daysTone, WINDOW_TONE } from './CertificateParts.js';
import classes from './Compliance.module.css';
import { type CertificateView, remarkLines, reminderStages } from './certificate-view.js';
import type {
  CertificateDetail,
  CertificateListItem,
  CertificatePage,
  OnBoardIssuance,
} from './types.js';

export const DETAIL_TABS = ['general', 'document', 'history', 'remarks'] as const;
export type DetailTab = (typeof DETAIL_TABS)[number];

/** Sertifikanın "Type" metni (referans "SOLAS"): ara düğüm, yoksa kategori adı. */
export function useTypeText() {
  const { t } = useTranslation();
  return (type: CertificateListItem['type']) =>
    type
      ? (type.subCategory ??
        t(`vesselCertificates.categories.${type.category}`, { defaultValue: type.category }))
      : '—';
}

function PageIcon({ page }: { page: Pick<CertificatePage, 'mimeType'> }) {
  const image = page.mimeType.startsWith('image/');
  const Icon = image ? IconPhoto : IconFileTypePdf;
  return (
    <span className={classes.fileIcon} data-kind={image ? 'image' : 'pdf'} aria-hidden>
      <Icon size={20} stroke={1.6} />
    </span>
  );
}

export interface CertificateDetailsProps {
  /** Seçili liste satırı; ofis kaydı gelmemiş gemide düzenlenen sertifikada ayrıntı yoktur. */
  item: CertificateListItem | null;
  detail: CertificateDetail | null;
  status: 'idle' | 'loading' | 'error' | 'ready';
  error?: string | undefined;
  onRetry?: () => void;
  issuance?: OnBoardIssuance | null;
  vesselName: string;
  scope: 'web' | 'ship';
  tab: DetailTab;
  onTab: (tab: DetailTab) => void;
  onViewPage: (index: number) => void;
  /** History sekmesinin denetim kaydı (kaynak biliyorsa). */
  onAudit?: (() => void) | undefined;
  /** Ofis onayı bekleyen gemi kaydının "Review & approve" düğmesi (ADR 0116 §2; yetkiyle). */
  onApprove?: (() => void) | undefined;
}

/**
 * "Certificate Details" (`gemi-sertifika-takibi.jpeg`): ikon, ad, tür, durum ve kalan gün; General,
 * Document, History, Remarks sekmeleri. Gemide sayfaların gemideki durumu (K34), geçmişin alan
 * ayrıntısı ofistedir (K37).
 */
export function CertificateDetails({
  item,
  detail,
  status,
  error,
  onRetry,
  issuance = null,
  vesselName,
  scope,
  tab,
  onTab,
  onViewPage,
  onAudit,
  onApprove,
}: CertificateDetailsProps) {
  const { t, i18n } = useTranslation();
  const k = (key: string, values: Record<string, unknown> = {}) =>
    t(`vesselCertificates.details.${key}`, values);
  const typeText = useTypeText();
  const locale = i18n.resolvedLanguage ?? 'en';

  if (!item) {
    return (
      <Panel title={k('title')}>
        {status === 'loading' ? (
          <DetailSkeleton />
        ) : (
          <EmptyState icon={<IconFileCertificate size={22} />} title={k('empty')} />
        )}
      </Panel>
    );
  }
  const tone = daysTone(item);
  const next = detail?.nextSurvey ?? item.nextSurvey;
  const pages = [...(detail?.pages ?? [])].sort((a, b) => a.sortOrder - b.sortOrder);
  const remarks = remarkLines(detail?.remarks ?? issuance?.remarks ?? null);

  const field = (label: string, value: ReactNode, note?: ReactNode) => (
    <div key={label}>
      <dt>{label}</dt>
      <dd>
        {value}
        {note ? <span className={classes.fieldNote}>{note}</span> : null}
      </dd>
    </div>
  );

  const general = (
    <>
      {item.officeEntryPending ? (
        <p className={classes.notice}>
          <IconRosetteDiscountCheck size={16} aria-hidden />
          <span>
            {k('onBoardNote')}
            {onApprove ? (
              <Button
                mt={6}
                size="xs"
                display="block"
                leftSection={<IconRosetteDiscountCheck size={14} />}
                onClick={onApprove}
              >
                {t('vesselCertificates.approval.review')}
              </Button>
            ) : null}
          </span>
        </p>
      ) : null}
      {item.superseded ? (
        <p className={classes.notice} data-tone="warning">
          <IconStackFront size={16} aria-hidden />
          {k('supersededNote')}
        </p>
      ) : null}
      <dl className={classes.fields}>
        {field(k('fields.number'), item.number)}
        {field(k('fields.name'), item.name)}
        {field(k('fields.type'), typeText(item.type))}
        {field(k('fields.subCategory'), item.type?.name ?? '—')}
        {field(k('fields.issuedBy'), item.issuedBy)}
        {field(k('fields.issueDate'), formatDay(item.issueDate))}
        {field(
          k('fields.expiryDate'),
          item.expiryDate ? formatDay(item.expiryDate) : k('noExpiry'),
        )}
        {detail?.anniversaryDate
          ? field(k('fields.anniversary'), formatDay(detail.anniversaryDate))
          : null}
        {field(
          k('fields.nextSurvey'),
          next
            ? k('nextSurveyValue', {
                date: formatDay(next.due),
                kind: t(`vesselCertificates.upcoming.kinds.${next.kind}`, {
                  defaultValue: next.kind,
                }),
              })
            : '—',
          next ? (
            <>
              <StatusBadge tone={WINDOW_TONE[next.state]}>
                {k(`windowStates.${next.state}`, {
                  date: formatDay(next.state === 'upcoming' ? next.from : next.to),
                })}
              </StatusBadge>{' '}
              {k('windowValue', { from: formatDay(next.from), to: formatDay(next.to) })}
              {next.source === 'override' && next.reason ? (
                <span className={classes.fieldNote}>
                  {k('overridden', { reason: next.reason })}
                </span>
              ) : null}
            </>
          ) : undefined,
        )}
        {field(k('fields.vessel'), vesselName || '—')}
        {field(k('fields.status'), <CertificateStatus item={item} />)}
        {field(
          k('fields.daysLeft'),
          <span className={classes.days} data-tone={tone ?? undefined}>
            {item.daysLeft === null ? k('noExpiry') : item.daysLeft}
          </span>,
        )}
      </dl>
    </>
  );

  const documentTab =
    status === 'loading' && !detail ? (
      <DetailSkeleton />
    ) : pages.length === 0 ? (
      <EmptyState title={k('pages.empty')} />
    ) : (
      <>
        <ul className={classes.files} aria-label={k('pages.title')}>
          {pages.map((page, index) => (
            <li key={page.id} className={classes.file}>
              <PageIcon page={page} />
              <span className={classes.detailText}>
                <UnstyledButton
                  className={classes.fileName}
                  onClick={() => onViewPage(index)}
                  aria-label={k('pages.view', { name: page.fileName })}
                >
                  {k('pages.page', { n: index + 1 })} · {page.fileName}
                </UnstyledButton>
                {page.onBoard ? (
                  <span className={classes.pageState} data-state={page.onBoard}>
                    {k(`pages.onBoard.${page.onBoard}`)}
                    {page.onBoard !== 'on_board' && page.downloadReason
                      ? ` · ${k('pages.queuedReason', { reason: page.downloadReason })}`
                      : ''}
                  </span>
                ) : null}
              </span>
              <span className={classes.fileMeta}>{formatFileSize(page.size, locale)}</span>
              <span />
            </li>
          ))}
        </ul>
        <Button
          mt={12}
          variant="light"
          leftSection={<IconEye size={16} aria-hidden />}
          onClick={() => onViewPage(0)}
        >
          {k('pages.viewAll')}
        </Button>
      </>
    );

  const history = (
    <>
      {detail && detail.chain.length > 0 ? (
        <>
          <h4 className={classes.sectionTitle}>{k('history.chain')}</h4>
          <ol className={classes.timeline}>
            {detail.chain.map((link) => (
              <li key={link.id} data-current={link.id === detail.id || undefined}>
                <strong>{link.number}</strong>
                <span className={classes.muted}>
                  {formatDay(link.issueDate)} – {formatDay(link.expiryDate)}
                  {link.id === detail.id ? ` · ${k('history.current')}` : ''}
                  {link.deleted ? ` · ${k('history.deleted')}` : ''}
                </span>
              </li>
            ))}
          </ol>
        </>
      ) : null}
      <h4 className={classes.sectionTitle}>{k('history.endorsements')}</h4>
      {detail && detail.endorsements.length > 0 ? (
        <ol className={classes.timeline}>
          {detail.endorsements.map((e) => (
            <li key={e.id} data-tone={e.warning ? 'warning' : undefined}>
              <strong>{formatDay(e.endorsedOn)}</strong>
              <span>
                {k('history.endorsementLine', {
                  kind: t(`vesselCertificates.dialogs.endorsement.kinds.${e.surveyKind}`, {
                    defaultValue: e.surveyKind,
                  }),
                  place: e.place,
                  surveyor: e.surveyor,
                })}
              </span>
              <span className={classes.muted}>
                {e.warning
                  ? k('history.outside')
                  : e.satisfies
                    ? k('history.satisfies', {
                        kind: t(`vesselCertificates.upcoming.kinds.${e.satisfies.kind}`),
                        from: formatDay(e.satisfies.from),
                        to: formatDay(e.satisfies.to),
                      })
                    : k('history.additional')}
              </span>
            </li>
          ))}
        </ol>
      ) : (
        <p className={classes.muted}>{k('history.noEndorsements')}</p>
      )}
      {detail?.issuance || issuance ? (
        <>
          <h4 className={classes.sectionTitle}>{k('history.issuance')}</h4>
          <IssuanceLine issuance={(detail?.issuance ?? issuance) as OnBoardIssuance} />
        </>
      ) : null}
      <h4 className={classes.sectionTitle}>{k('history.audit')}</h4>
      {scope === 'ship' ? <p className={classes.muted}>{k('history.officeHistory')}</p> : null}
      {onAudit && detail ? (
        <Button variant="default" size="xs" onClick={onAudit}>
          {k('history.openAudit')}
        </Button>
      ) : null}
    </>
  );

  const remarksTab =
    remarks.length === 0 ? (
      <EmptyState title={k('remarks.empty')} />
    ) : (
      <ol className={classes.timeline}>
        {remarks.map((line, index) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: satırlar yalnız eklenir, sırası değişmez
          <li key={index}>
            {line.person || line.date ? (
              <span className={classes.muted}>
                {k('remarks.by', { person: line.person ?? '—', date: formatDay(line.date) })}
              </span>
            ) : null}
            <span className={classes.remarkText}>{line.text}</span>
          </li>
        ))}
      </ol>
    );

  return (
    <Panel title={k('title')}>
      <div className={classes.detailHead}>
        <span className={classes.docIcon} aria-hidden>
          <IconFileDescription size={30} stroke={1.5} />
        </span>
        <div className={classes.detailText}>
          <h3 className={classes.detailName}>{item.name}</h3>
          <span className={classes.detailType}>{typeText(item.type)}</span>
          <CertificateStatus item={item} />
        </div>
        <div className={classes.daysBox} data-tone={tone ?? undefined}>
          <span>{k('daysLeft')}</span>
          <strong>{item.daysLeft === null ? '—' : item.daysLeft}</strong>
        </div>
      </div>
      {status === 'error' ? (
        <EmptyState
          tone="danger"
          title={t('vesselCertificates.error')}
          description={error}
          action={
            onRetry ? (
              <Button
                size="xs"
                variant="light"
                leftSection={<IconRefresh size={14} />}
                onClick={onRetry}
              >
                {t('vesselCertificates.retry')}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <Tabs
          value={tab}
          onChange={(value) => {
            if (value) onTab(value as DetailTab);
          }}
          className={classes.tabs}
          keepMounted={false}
        >
          <Tabs.List aria-label={k('tabs.label')}>
            {DETAIL_TABS.map((key) => (
              <Tabs.Tab key={key} value={key}>
                {k(`tabs.${key}`)}
              </Tabs.Tab>
            ))}
          </Tabs.List>
          <Tabs.Panel value="general" pt={12}>
            {general}
          </Tabs.Panel>
          <Tabs.Panel value="document" pt={12}>
            {documentTab}
          </Tabs.Panel>
          <Tabs.Panel value="history" pt={12}>
            {history}
          </Tabs.Panel>
          <Tabs.Panel value="remarks" pt={12}>
            {remarksTab}
          </Tabs.Panel>
        </Tabs>
      )}
    </Panel>
  );
}

function IssuanceLine({ issuance }: { issuance: OnBoardIssuance }) {
  const { t } = useTranslation();
  return (
    <ol className={classes.timeline}>
      <li data-current>
        <strong>{formatDay(issuance.issueDate)}</strong>
        <span>
          {t('vesselCertificates.details.history.issuanceLine', {
            number: issuance.number,
            issuedBy: issuance.issuedBy,
            date: formatDay(issuance.expiryDate),
          })}
        </span>
        {issuance.shortTerm ? (
          <span className={classes.muted}>{t('vesselCertificates.details.history.shortTerm')}</span>
        ) : null}
      </li>
    </ol>
  );
}

function DetailSkeleton() {
  return (
    <div className={classes.skeletonRows} aria-hidden>
      {[0, 1, 2, 3, 4, 5].map((n) => (
        <Skeleton key={n} height={16} />
      ))}
    </div>
  );
}

/** "Attached Documents" (referans): taranmış sayfalar, boyut ve indirme. */
export function AttachedDocuments({
  pages,
  onView,
  onDownload,
}: {
  pages: readonly CertificatePage[] | null;
  onView: (index: number) => void;
  onDownload: (page: CertificatePage) => void;
}) {
  const { t, i18n } = useTranslation();
  const k = (key: string, values: Record<string, unknown> = {}) =>
    t(`vesselCertificates.attached.${key}`, values);
  const locale = i18n.resolvedLanguage ?? 'en';
  const sorted = pages ? [...pages].sort((a, b) => a.sortOrder - b.sortOrder) : null;
  return (
    <Panel title={k('title')} icon={<IconPaperclip size={18} />}>
      {sorted === null ? (
        <DetailSkeleton />
      ) : sorted.length === 0 ? (
        <p className={classes.muted}>{k('empty')}</p>
      ) : (
        <ul className={classes.files} aria-label={k('title')}>
          {sorted.map((page, index) => {
            const missing = page.onBoard !== undefined && page.onBoard !== 'on_board';
            return (
              <li key={page.id} className={classes.file}>
                <PageIcon page={page} />
                <span className={classes.detailText}>
                  <UnstyledButton className={classes.fileName} onClick={() => onView(index)}>
                    {page.fileName}
                  </UnstyledButton>
                  {missing && page.onBoard ? (
                    <span className={classes.pageState} data-state={page.onBoard}>
                      {t(`vesselCertificates.details.pages.onBoard.${page.onBoard}`)}
                    </span>
                  ) : null}
                </span>
                <span className={classes.fileMeta}>{formatFileSize(page.size, locale)}</span>
                <Tooltip label={k('download', { name: page.fileName })} withinPortal>
                  <ActionIcon
                    variant="subtle"
                    aria-label={k('download', { name: page.fileName })}
                    disabled={missing}
                    onClick={() => onDownload(page)}
                  >
                    <IconDownload size={17} />
                  </ActionIcon>
                </Tooltip>
              </li>
            );
          })}
        </ul>
      )}
    </Panel>
  );
}

export interface QuickActionsProps {
  scope: 'web' | 'ship';
  hasPages: boolean;
  canRemark: boolean;
  canEndorse: boolean;
  onViewDocument: () => void;
  onDownload: () => void;
  onRemark: () => void;
  onEndorse: () => void;
  onReminder: () => void;
}

/**
 * "Quick Actions" (referans): View Document, Download, Add Remark, Create Reminder. Gemide "Add
 * Remark" yerine "Record Endorsement" (K28).
 */
export function QuickActions({
  scope,
  hasPages,
  canRemark,
  canEndorse,
  onViewDocument,
  onDownload,
  onRemark,
  onEndorse,
  onReminder,
}: QuickActionsProps) {
  const { t } = useTranslation();
  const k = (key: string) => t(`vesselCertificates.quickActions.${key}`);
  return (
    <Panel title={k('title')} icon={<IconZoomCheck size={18} />}>
      <div className={classes.actionsGrid}>
        <Button
          variant="default"
          leftSection={<IconEye size={17} aria-hidden />}
          disabled={!hasPages}
          onClick={onViewDocument}
        >
          {k('viewDocument')}
        </Button>
        <Button
          variant="default"
          leftSection={<IconDownload size={17} aria-hidden />}
          disabled={!hasPages}
          onClick={onDownload}
        >
          {k('download')}
        </Button>
        {scope === 'ship' ? (
          <Button
            variant="default"
            leftSection={<IconRosetteDiscountCheck size={17} aria-hidden />}
            disabled={!canEndorse}
            onClick={onEndorse}
          >
            {k('endorse')}
          </Button>
        ) : (
          <Button
            variant="default"
            leftSection={<IconMessagePlus size={17} aria-hidden />}
            disabled={!canRemark}
            onClick={onRemark}
          >
            {k('addRemark')}
          </Button>
        )}
        <Button
          variant="default"
          leftSection={<IconBell size={17} aria-hidden />}
          onClick={onReminder}
        >
          {k('reminder')}
        </Button>
      </div>
    </Panel>
  );
}

export interface QuickLinksProps {
  scope: 'web' | 'ship';
  view: CertificateView;
  canAdd: boolean;
  canIssue: boolean;
  onAdd: () => void;
  onIssue: () => void;
  onView: (view: CertificateView) => void;
}

/** "Quick Links" (referans): Add New Certificate, Certificate Calendar, Survey Schedule, Regulatory. */
export function CertificateQuickLinks({
  scope,
  view,
  canAdd,
  canIssue,
  onAdd,
  onIssue,
  onView,
}: QuickLinksProps) {
  const { t } = useTranslation();
  const k = (key: string) => t(`vesselCertificates.quickLinks.${key}`);
  const links: { key: string; icon: ReactNode; active?: boolean; onClick: () => void }[] = [];
  if (scope === 'web' && canAdd) {
    links.push({ key: 'add', icon: <IconPlus size={16} />, onClick: onAdd });
  }
  if (scope === 'ship' && canIssue) {
    links.push({ key: 'issue', icon: <IconRosetteDiscountCheck size={16} />, onClick: onIssue });
  }
  links.push(
    {
      key: 'calendar',
      icon: <IconCalendarEvent size={16} />,
      active: view === 'calendar',
      onClick: () => onView(view === 'calendar' ? 'list' : 'calendar'),
    },
    {
      key: 'surveys',
      icon: <IconCalendarStats size={16} />,
      active: view === 'surveys',
      onClick: () => onView(view === 'surveys' ? 'list' : 'surveys'),
    },
    {
      key: 'regulatory',
      icon: <IconChecklist size={16} />,
      active: view === 'regulatory',
      onClick: () => onView(view === 'regulatory' ? 'list' : 'regulatory'),
    },
  );
  return (
    <Panel title={k('title')} icon={<IconLink size={18} />}>
      <ul className={classes.links}>
        {links.map((link) => (
          <li key={link.key}>
            <UnstyledButton
              className={classes.link}
              data-active={link.active || undefined}
              aria-pressed={link.active === undefined ? undefined : link.active}
              onClick={link.onClick}
            >
              <span className={classes.linkIcon} aria-hidden>
                {link.icon}
              </span>
              {k(link.key)}
            </UnstyledButton>
          </li>
        ))}
      </ul>
    </Panel>
  );
}

/**
 * "Create Reminder": hatırlatmalar kişisel değil, otomatiktir (K30): bitişten 90, 60 ve 30 gün
 * önce bildirim; kademeler koddan.
 */
export function ReminderDialog({
  opened,
  item,
  onClose,
}: {
  opened: boolean;
  item: CertificateListItem | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const k = (key: string, values: Record<string, unknown> = {}) =>
    t(`vesselCertificates.reminder.${key}`, values);
  const stages = reminderStages(item?.daysLeft ?? null);
  const reached = stages.filter((s) => s.reached).at(-1);
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<span className={classes.drawerTitle}>{k('title')}</span>}
      closeButtonProps={{ 'aria-label': k('close') }}
    >
      {item ? (
        <>
          <p className={classes.muted}>{item.name}</p>
          <p>{k('body')}</p>
          {item.daysLeft === null ? (
            <p className={classes.notice}>{k('noExpiry')}</p>
          ) : (
            <>
              <p className={classes.notice} data-tone={item.daysLeft < 0 ? 'warning' : undefined}>
                {item.daysLeft < 0 ? (
                  <IconAlertTriangle size={16} aria-hidden />
                ) : (
                  <IconBell size={16} aria-hidden />
                )}
                {item.daysLeft < 0
                  ? k('expired')
                  : reached
                    ? k('current', { days: reached.days })
                    : k('none', { days: item.daysLeft })}
              </p>
              <dl className={classes.fields} aria-label={k('stages')}>
                {stages.map((stage) => (
                  <div key={stage.days}>
                    <dt>{k('stage', { days: stage.days })}</dt>
                    <dd>
                      <StatusBadge tone={stage.reached ? 'warning' : 'neutral'}>
                        {stage.reached ? k('reached') : k('pending')}
                      </StatusBadge>
                    </dd>
                  </div>
                ))}
              </dl>
            </>
          )}
          <div className={classes.dialogActions}>
            <Button onClick={onClose}>{k('close')}</Button>
          </div>
        </>
      ) : null}
    </Modal>
  );
}
